import { EventContentArg } from '@fullcalendar/core';
import dayjs from 'dayjs';
import { CalendarCheck, CalendarCog, CalendarIcon, Cog, MessageSquareIcon, Notebook, StethoscopeIcon, StickyNoteIcon, UserIcon, UserPlus, Wifi, WifiOff } from 'lucide-react';
import React, { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useShallow } from 'zustand/react/shallow';
import { Button } from '~/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import LocalDateTime from '~/lib/localdatetime';
import { useAppointmentsCalendarStore } from '~/stores/appointments-calendar-store';
import { useCreateOrModifyAppointmentStore } from '~/stores/create-appointment-store';
import { useScheduleEditorModalStore } from '~/stores/schedule-editor-modal-store';
import { useUserStore } from '~/stores/user-store';
import { AppointmentStatus, getAppointmentStatusLabel } from '~/types/server-types/AppointmentStatus';

type EventStep = {
    id: string;
    start: Date;
    end: Date;
    name: string;
    resourceIds: string[];
    treatments: string[];
    practitioners: {
        id: number;
        firstName: string;
        lastName: string;
        fullName: string;
    }[];
    rooms: {
        id: number;
        name: string;
    }[];
    equipment: {
        id: number;
        name: string;
    }[];
};

type Props = {
    info: EventContentArg;
};

function EventPopover({ info }: Props) {
    const { timezone } = useUserStore(
        useShallow((state) => ({
            timezone: state.user?.timezone,
        }))
    );

    const { setScheduleEditorModalOpen, setScheduleId, setScheduleType, setScheduleClickedDateRange } = useScheduleEditorModalStore(
        useShallow((state) => ({
            setScheduleEditorModalOpen: state.setOpen,
            setScheduleId: state.setScheduleId,
            setScheduleType: state.setType,
            setScheduleClickedDateRange: state.setClickedDateRange,
        }))
    );

    const { event } = info;

    const isScheduleEvent = useMemo(() => event.id.startsWith("schedule-"), [event])

    const [showTimes, setShowTimes] = useState(false);
    const [elementPosition, setElementPosition] = useState({ top: 0, left: 0, height: 0 });
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [showSteps, setShowSteps] = useState(false);
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    const status: AppointmentStatus | undefined = useMemo(() => event.extendedProps?.metadata?.status, [event])
    const isCustomerFirstAppointment: boolean | undefined = useMemo(() => event.extendedProps?.metadata?.isCustomerFirstAppointment, [event])
    const customerNote: string | undefined = useMemo(() => event.extendedProps?.metadata?.customerNote, [event])
    const messageFromCustomer: string | undefined = useMemo(() => event.extendedProps?.metadata?.messageFromCustomer, [event])
    const appointmentNotes: string | undefined = useMemo(() => event.extendedProps?.metadata?.appointmentNotes, [event])
    const appointmentBookedType: 'customer-booked' | 'company-booked' | 'unknown' | undefined = useMemo(() => event.extendedProps?.metadata?.bookedType, [event])

    const hasAnyNote = useMemo(() => {
        return (customerNote && customerNote.trim().length > 0) ||
            (messageFromCustomer && messageFromCustomer.trim().length > 0) ||
            (appointmentNotes && appointmentNotes.trim().length > 0);
    }, [customerNote, messageFromCustomer, appointmentNotes])

    const steps: EventStep[] | undefined = event.extendedProps?.metadata?.steps;
    const hasMultipleSteps = steps && steps.length > 1;

    const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setElementPosition({
            top: rect.top,
            left: rect.left,
            height: rect.height,
        });
        setShowTimes(true);
        if (hasMultipleSteps) {
            setShowSteps(true);
        }

        // Set a timer to open the popover after 400ms
        timerRef.current = setTimeout(() => {
            setIsPopoverOpen(true);
        }, 400);
    };

    const handleMouseLeave = () => {
        setShowTimes(false);
        setShowSteps(false);

        // Clear the timer if mouse leaves before popover opens
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }

        // Ensure popover closes immediately when mouse leaves
        setIsPopoverOpen(false);
    };

    const handleClick = () => {
        if (!!event.extendedProps?.metadata?.variant && event.extendedProps.metadata.variant === "reception-availability") {
            return;
        }

        const appointmentId = event.extendedProps?.metadata?.appointmentId as string | undefined;
        if (appointmentId) {
            // Update stores
            useCreateOrModifyAppointmentStore.setState({
                appointmentId: appointmentId,
                mode: 'modify',
                notifyCustomer: false,
            });

            useAppointmentsCalendarStore.setState({
                createOrModifyAppointmentDrawerOpen: true,
            });
        } else if (isScheduleEvent && event.extendedProps.metadata.type === 'BLOCKED') {
            handleOpenScheduleEditor();
        }
    }

    // Track if mouse is over the event or popover
    const [isMouseOverEventOrPopover, setIsMouseOverEventOrPopover] = useState(false);

    // Use a global mouse move handler to detect when mouse moves too quickly
    useEffect(() => {
        const handleGlobalMouseMove = (e: MouseEvent) => {
            // Only check if the popover should be open when it's currently open
            // or when we're tracking mouse movement for potential opening
            if (isPopoverOpen || timerRef.current) {
                const eventElement = document.querySelector('[data-popover-trigger="true"]');
                const popoverElement = document.querySelector('[data-state="open"][role="dialog"]');

                if (eventElement || popoverElement) {
                    let isOverEvent = false;
                    let isOverPopover = false;

                    if (eventElement) {
                        const eventRect = eventElement.getBoundingClientRect();
                        isOverEvent = e.clientX >= eventRect.left &&
                            e.clientX <= eventRect.right &&
                            e.clientY >= eventRect.top &&
                            e.clientY <= eventRect.bottom;
                    }

                    if (popoverElement) {
                        const popoverRect = popoverElement.getBoundingClientRect();
                        isOverPopover = e.clientX >= popoverRect.left &&
                            e.clientX <= popoverRect.right &&
                            e.clientY >= popoverRect.top &&
                            e.clientY <= popoverRect.bottom;
                    }

                    const isOver = isOverEvent || isOverPopover;

                    // Only update state when there's an actual change to prevent re-renders
                    if (isOver !== isMouseOverEventOrPopover) {
                        setIsMouseOverEventOrPopover(isOver);
                    }

                    // If mouse is not over either element, close the popover
                    if (!isOver && isPopoverOpen) {
                        setIsPopoverOpen(false);
                        setShowTimes(false);
                        setShowSteps(false);
                    }
                }
            }
        };

        // Add a debounced version of the handler to prevent too many updates
        let timeout: NodeJS.Timeout | null = null;
        const debouncedHandler = (e: MouseEvent) => {
            if (timeout) {
                clearTimeout(timeout);
            }
            timeout = setTimeout(() => {
                handleGlobalMouseMove(e);
            }, 50);
        };

        window.addEventListener('mousemove', debouncedHandler);

        return () => {
            window.removeEventListener('mousemove', debouncedHandler);
            if (timeout) {
                clearTimeout(timeout);
            }
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, [isPopoverOpen, isMouseOverEventOrPopover]);

    const flattenedResources = useMemo(() => {
        const treatments: Set<string> = new Set<string>();
        const practitioners: {
            id: number;
            firstName: string;
            fullName: string;
            lastName: string;
        }[] = [];
        const rooms: {
            id: number;
            name: string;
        }[] = [];
        const equipment: {
            id: number;
            name: string;
        }[] = [];

        const steps: {
            id: string;
            start: string;
            end: string;
            treatments: string[];
            practitioners: any[];
            rooms: any[];
            equipment: any[];
        }[] = event?.extendedProps?.metadata?.steps || [];
        for (let i = 0; i < steps.length; i++) {
            const step = steps[i];
            if (step.treatments) {
                step.treatments.forEach((treatment) => {
                    treatments.add(treatment);
                })
            }
            if (step.practitioners) {
                practitioners.push(...step.practitioners);
            }
            if (step.rooms) {
                rooms.push(...step.rooms);
            }
            if (step.equipment) {
                equipment.push(...step.equipment);
            }
        }

        const uniqueTreatments = Array.from(treatments);

        // Filter to only have unique resources by id
        const uniquePractitioners = Array.from(
            new Map(practitioners.map(p => [p.id, p])).values()
        );

        const uniqueRooms = Array.from(
            new Map(rooms.map(r => [r.id, r])).values()
        );

        const uniqueEquipment = Array.from(
            new Map(equipment.map(e => [e.id, e])).values()
        );

        return {
            treatments: uniqueTreatments,
            practitioners: uniquePractitioners,
            rooms: uniqueRooms,
            equipment: uniqueEquipment
        }
    }, [event]);

    // Format times once to avoid recalculation
    const { startTimeFormatted, endTimeFormatted } = useMemo(() => {
        const startTime = dayjs.tz(event.start, timezone);
        const endTime = dayjs.tz(event.end, timezone);
        return {
            startTimeFormatted: startTime.format('HH:mm'),
            endTimeFormatted: endTime.format('HH:mm')
        }
    }, [event.start, event.end]);

    const statusStyles = useMemo(() => {
        if (!status) {
            return {};
        }
        const styles: CSSProperties = {}

        if (status === 'CANCELLED' || status === 'DECLINED' || status === 'NO_SHOW' || status === 'CALLED_OFF') {
            styles.borderColor = '#dddddd !important';
            styles.backgroundColor = '#ffffff';
        } else if (status === 'REQUESTED') {
            styles.backgroundColor = '#deebd2';
            styles.background = 'repeating-linear-gradient( 45deg, #c4dbae, #c4dbae 7px, #deebd2 7px, #deebd2 15px )';
        }

        return styles;
    }, [status]);

    const handleOpenScheduleEditor = () => {
        const scheduleEntryId: string | undefined = event.extendedProps.metadata.scheduleEntryId;
        const type: 'practitioner' | 'equipment' | null = event.id.includes("schedule-practitioner") ? 'practitioner' : event.id.includes("schedule-equipment") ? 'equipment' : null;
        const dateClickedStart = event.start;
        const dateClickedEnd = event.end;
        const dateClickedRange = !!dateClickedStart && !!dateClickedEnd ? {
            start: LocalDateTime.fromUTCDate(dateClickedStart, 'Europe/Berlin'),
            end: LocalDateTime.fromUTCDate(dateClickedEnd, 'Europe/Berlin'),
        } : null;
        console.log(scheduleEntryId, type);
        if (!!scheduleEntryId && !!type) {
            setScheduleId(scheduleEntryId)
            setScheduleType(type);
            setScheduleEditorModalOpen(true);
            setScheduleClickedDateRange(dateClickedRange);
        }
    }

    return (
        <Popover open={isPopoverOpen}>
            <PopoverTrigger asChild>
                <div
                    className='relative w-full h-full font-[Helvetica, Calibri] overflow-hidden whitespace-nowrap max-h-full group z-[2] hover:z-50'
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onClick={handleClick}
                    data-popover-trigger="true"
                    style={statusStyles}
                >
                    {/* Start time tooltip - rendered in portal to avoid z-index issues */}
                    {showTimes &&
                        event.start &&
                        createPortal(
                            <div
                                className='hidden pointer-fine:block fixed bg-white border border-gray-100 rounded-sm text-gray-700 font-semibold text-xs py-0.5 px-1 shadow-md z-[9999]'
                                style={{
                                    top: elementPosition.top - 22,
                                    left: elementPosition.left,
                                    pointerEvents: 'none',
                                }}
                            >
                                {startTimeFormatted}
                            </div>,
                            document.body
                        )}

                    {(status === 'DECLINED' || status === 'CANCELLED' || status === 'NO_SHOW') && (
                        <div className='absolute p-0.5 rounded-full bg-gray-600 right-2 top-2'>
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="#eeeeee" viewBox="0 0 256 256"><path d="M53.92,34.62A8,8,0,0,0,48,32,16,16,0,0,0,32,48V208a16,16,0,0,0,16,16H208a8,8,0,0,0,5.92-13.38ZM73.55,80H48V51.88ZM48,208V96H88.1L189.92,208ZM224,48V177.23a8,8,0,1,1-16,0V96H134.88a8,8,0,0,1,0-16H208V48H184v8a8,8,0,0,1-16,0V48H91.25a8,8,0,0,1,0-16H168V24a8,8,0,0,1,16,0v8h24A16,16,0,0,1,224,48Z"></path></svg>
                        </div>
                    )}

                    {status === 'REQUESTED' && (
                        <div className='absolute p-0.5 rounded-full bg-primary-400 right-0 bottom-0'>
                            <CalendarCog size={12} color='#eeeeee' />
                        </div>
                    )}

                    <div className='absolute bottom-0 left-0 flex flex-row gap-1'>
                        {hasAnyNote && (
                            <div className='p-0.5 rounded-full bg-gray-600'>
                                <StickyNoteIcon className='w-3 h-3 text-[#eee]' />
                            </div>
                        )}

                        {isCustomerFirstAppointment === true && (
                            <div className='p-0.5 rounded-full bg-gray-600'>
                                <UserPlus className='w-3 h-3 text-[#eee]' />
                            </div>
                        )}

                        {appointmentBookedType === 'customer-booked' && (
                            <div className='p-0.5 rounded-full bg-gray-600'>
                                <Wifi className='w-3 h-3 text-[#eee]' />
                            </div>
                        )}

                        {appointmentBookedType === 'company-booked' && (
                            <div className='p-0.5 rounded-full bg-gray-600'>
                                <WifiOff className='w-3 h-3 text-[#eee]' />
                            </div>
                        )}
                    </div>

                    {!showSteps && (
                        <div className='relative flex flex-row flex-wrap overflow-hidden whitespace-nowrap max-h-full text-[10px]'>
                            <div className='truncate'>
                                {event.extendedProps.metadata?.customerName && (
                                    <span className='text-gray-700 font-semibold overflow-hidden'>{event.extendedProps.metadata?.customerName}</span>
                                )}
                                {event.extendedProps.metadata?.appointmentShort && (
                                    <span className='text-gray-700 font-semibold overflow-hidden ml-1'>({event.extendedProps.metadata?.appointmentShort})</span>
                                )}
                            </div>
                        </div>
                    )}

                    {showSteps && hasMultipleSteps && (
                        <div className='flex flex-col h-full w-full'>
                            {steps
                                .sort((a, b) => {
                                    const dateA = new Date(String(a));
                                    const dateB = new Date(String(b));
                                    return dateA.getTime() - dateB.getTime();
                                })
                                .map((step, index) => {
                                    if (index === 0) {
                                        return (
                                            <div
                                                key={step.id}
                                                className='truncate flex-1 text-[10px] text-gray-700 font-semibold overflow-hidden'
                                                style={{
                                                    borderBottom: index < steps.length - 1 ? '1px solid rgba(255, 255, 255, 0.3)' : 'none',
                                                }}
                                            >
                                                <div className='flex flex-row'>
                                                    {event.extendedProps.metadata?.customerName && (
                                                        <span className='text-gray-700 font-semibold overflow-hidden'>{event.extendedProps.metadata.customerName}</span>
                                                    )}
                                                    {event.extendedProps.metadata?.appointmentShort && (
                                                        <span className='text-gray-700 font-semibold overflow-hidden ml-1'>({event.extendedProps.metadata?.appointmentShort})</span>
                                                    )}
                                                </div>
                                                <span className='text-gray-700 font-semibold overflow-hidden'>{step.name}</span>
                                            </div>
                                        );
                                    }
                                    return (
                                        <div
                                            key={step.id}
                                            className='flex-1 truncate text-[10px] text-gray-700 font-semibold overflow-hidden'
                                            style={{
                                                borderBottom: index < steps.length - 1 ? '1px solid rgba(255, 255, 255, 0.3)' : 'none',
                                            }}
                                        >
                                            {step.name}
                                        </div>
                                    );
                                })}
                        </div>
                    )}

                    {/* End time tooltip - rendered in portal to avoid z-index issues */}
                    {showTimes &&
                        event.end &&
                        createPortal(
                            <div
                                className='hidden pointer-fine:block fixed bg-white border border-gray-100 rounded-sm text-gray-700 font-semibold text-xs py-0.5 px-1 shadow-md z-[9999]'
                                style={{
                                    top: elementPosition.top + elementPosition.height - 2,
                                    left: elementPosition.left,
                                    pointerEvents: 'none',
                                }}
                            >
                                {endTimeFormatted}
                            </div>,
                            document.body
                        )}


                    {event.extendedProps.metadata?.type && event.extendedProps.metadata.type === 'BLOCKED' && isScheduleEvent && (
                        <div className='p-0.5 w-full h-full flex flex-col gap-1 flex-wrap truncate select-none overflow-hidden whitespace-wrap max-h-full text-[11px]'>
                            {(!!event.extendedProps.metadata?.blockedType || !!event.extendedProps.metadata?.description) && (
                                <span className='!text-white !font-bold overflow-hidden leading-normal'>{!!event.extendedProps.metadata?.blockedType ? `${event.extendedProps.metadata.blockedType} - ` : ''}{!!event.extendedProps.metadata?.description ? event.extendedProps.metadata.description : ''}</span>
                            )}
                            {event.start && event.end && (
                                <span className='text-white !font-bold overflow-hidden leading-normal'>
                                    {event?.start && dayjs(event.start).tz(timezone || 'Europe/Berlin').format('HH:mm')} {' bis '}
                                    {event?.end && dayjs(event.end).tz(timezone || 'Europe/Berlin').format('HH:mm')} Uhr
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </PopoverTrigger>
            <PopoverContent
                arrowPadding={2}
                side='right'
                className='w-80 hidden pointer-fine:block'
                onMouseEnter={() => setIsPopoverOpen(true)}
                onMouseLeave={() => setIsPopoverOpen(false)}
            >
                {isScheduleEvent ? (
                    <div className='p-1'>
                        <div className='p-1 space-y-2'>
                            <div className='border-b pb-2 mb-2'>
                                <h4 className='font-semibold text-primary'>Verfügbarkeiten</h4>
                                <div className='text-xs text-muted-foreground mt-1 flex items-center'>
                                    <CalendarIcon className='h-3 w-3 mr-1' />
                                    <span>
                                        {event?.start && dayjs(event.start).tz(timezone || 'Europe/Berlin').format('DD. MMM YYYY, HH:mm')} {' bis '}
                                        {event?.end && dayjs(event.end).tz(timezone || 'Europe/Berlin').format('DD. MMM YYYY, HH:mm')} Uhr
                                    </span>
                                </div>
                                <div className='text-xs text-muted-foreground mt-1 flex items-center'>
                                    <StethoscopeIcon className='h-3 w-3 mr-1' />
                                    <span>
                                        {event.getResources().map((resource) => (
                                            resource?.title || ''
                                        )).join(", ")}
                                    </span>
                                </div>
                            </div>

                            {event.extendedProps.metadata.description && (
                                <div className='mt-2'>
                                    <h5 className='text-xs font-medium text-muted-foreground mb-1'>Beschreibung</h5>
                                    <div className='text-sm p-2 rounded-md bg-secondary'>{event.extendedProps.metadata.description}</div>
                                </div>
                            )}

                            {/* {event.extendedProps.metadata.type !== 'AVAILABLE' ? (
                                <div className='flex mt-4'>
                                    <Button variant='outline' onClick={() => handleOpenScheduleEditor()}>
                                        Bearbeiten
                                    </Button>
                                </div>
                            ) : null} */}
                        </div>
                    </div>
                ) : (
                    <div className='p-1'>
                        <div className='mb-3 border-b pb-2'>
                            <h4 className='font-semibold text-primary'>Termindaten</h4>
                            <div className='text-xs text-muted-foreground mt-1 flex items-center'>
                                <CalendarIcon className='h-3 w-3 mr-1' />

                                <span>
                                    {event?.start && dayjs(event.start).tz(timezone || 'Europe/Berlin').format('DD MMM YYYY, HH:mm')} -
                                    {event?.end && dayjs(event.end).tz(timezone || 'Europe/Berlin').format('HH:mm')}
                                </span>
                            </div>

                            {status && (
                                <span className='text-xs text-muted-foreground mt-1 flex items-center'>
                                    {getAppointmentStatusLabel(status)}
                                </span>
                            )}
                        </div>

                        <div className='space-y-3'>
                            {event.extendedProps.metadata?.customerName && (
                                <div className='flex items-start gap-2'>
                                    <UserIcon className='h-4 w-4 text-primary mt-0.5 flex-shrink-0' />
                                    <div>
                                        <h5 className='text-xs font-medium text-muted-foreground'>Kunde</h5>
                                        <div className='text-sm font-medium'>{event.extendedProps.metadata.customerName}</div>
                                        {event.extendedProps.metadata?.customerEmail && (
                                            <div className='text-xs text-muted-foreground'>{event.extendedProps.metadata.customerEmail}</div>
                                        )}
                                        {event.extendedProps.metadata?.customerPhone && (
                                            <div className='text-xs text-muted-foreground'>{event.extendedProps.metadata.customerPhone}</div>
                                        )}
                                    </div>
                                </div>
                            )}


                            {flattenedResources.treatments.length > 0 && (
                                <div className='flex items-start gap-2'>
                                    <CalendarCheck className='h-4 w-4 text-primary mt-0.5 flex-shrink-0' />
                                    <div>
                                        <h5 className='text-xs font-medium text-muted-foreground'>Behandlung</h5>
                                        <div className='space-y-1'>
                                            {flattenedResources.treatments.map((treatment, index: number) => (
                                                <div key={`treatment_${index}`} className='text-sm font-medium'>
                                                    {treatment}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}


                            {flattenedResources.practitioners.length > 0 && (
                                <div className='flex items-start gap-2'>
                                    <StethoscopeIcon className='h-4 w-4 text-primary mt-0.5 flex-shrink-0' />
                                    <div>
                                        <h5 className='text-xs font-medium text-muted-foreground'>Behandler</h5>
                                        <div className='space-y-1'>
                                            {flattenedResources.practitioners.map((practitioner: any, index: number) => (
                                                <div key={`practitioner_${index}`} className='text-sm font-medium'>
                                                    {practitioner.firstName} {practitioner.lastName}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {flattenedResources.equipment.length > 0 && (
                                <div className='flex items-start gap-2'>
                                    <Cog className='h-4 w-4 text-primary mt-0.5 flex-shrink-0' />
                                    <div>
                                        <h5 className='text-xs font-medium text-muted-foreground'>Geräte</h5>
                                        <div className='space-y-1'>
                                            {flattenedResources.equipment.map((equipment: any, index: number) => (
                                                <div key={`equipment_${index}`} className='text-sm font-medium'>
                                                    {equipment.name}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {flattenedResources.rooms.length > 0 && (
                                <div className='flex items-start gap-2'>
                                    <Cog className='h-4 w-4 text-primary mt-0.5 flex-shrink-0' />
                                    <div>
                                        <h5 className='text-xs font-medium text-muted-foreground'>Räume</h5>
                                        <div className='space-y-1'>
                                            {flattenedResources.rooms.map((room: any, index: number) => (
                                                <div key={`room_${index}`} className='text-sm font-medium'>
                                                    {room.name}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {customerNote && customerNote.trim().length > 0 && (
                                <div className='flex items-start gap-2 mt-2'>
                                    <StickyNoteIcon className='h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0' />
                                    <div>
                                        <h5 className='text-xs font-medium text-muted-foreground mb-1'>Notes</h5>
                                        <div className='text-sm bg-amber-50 p-2 rounded-md border border-amber-100'>{customerNote.trim()}</div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </PopoverContent>
        </Popover>
    );
}

export default React.memo(EventPopover);
